import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription, throwError } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UserHTTPService } from 'src/app/data/http/user-http.service';
import {
  CenterImageUploadResponse,
  DiscourseConnectResponse,
} from 'src/app/data/http/user-http.service';
import { TokenDto } from 'src/app/data/http/dto/user/token-dto.model';
import { UserDto } from 'src/app/data/http/dto/user/user-dto.model';
import { LocalStorageService } from 'src/app/data/storage/local-storage.service';
import { SessionStorageService } from 'src/app/data/storage/session-storage.service';
import { UserMapperService } from '../mappers/user-mapper.service';
import { User } from '../models/user.model';
import { ErrorModel, UnauthorizedErrorModel } from '../error/error.model';
import { Result } from '../result.model';
import { BaseService } from './base.service';
import { BoothDto } from 'src/app/data/http/dto/user/booth-dto.model';
import { ResetPasswordDto } from 'src/app/data/http/dto/reset-password-dto.model';
import { VerifyEmailDto } from 'src/app/data/http/dto/user/verify-email-dto.model';
import { CenterDto } from 'src/app/data/http/dto/user/center-dto.model';
import {
  SocialAuthProvider,
  SocialAuthResponseDto,
} from 'src/app/data/http/dto/user/social-auth-dto.model';

export type UserType = User | undefined;

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseService {
  
  // public fields
  readonly currentUser$: Observable<UserType>;
  private readonly currentUserSubject: BehaviorSubject<UserType>;

  get currentUserValue(): UserType {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserType) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private userHttpService: UserHTTPService,
    localStorageService: LocalStorageService,
    private userMapperService: UserMapperService,
    sessionStorageService: SessionStorageService,
    router: Router
  ) {
    super(localStorageService, sessionStorageService, router);
    this.currentUserSubject = new BehaviorSubject<UserType>(
      this.sessionStorageService.getUser()
    );
    this.currentUser$ = this.currentUserSubject.asObservable();
  }

verifyToken(boothToken: string): Observable<boolean> {
  this.getUser();

  this.isLoadingSubject.next(true);

  return this.userHttpService.verifyToken(boothToken).pipe(
    map(response => response),
    catchError(() => of(false)),
    finalize(() => this.isLoadingSubject.next(false))
  );
}

  socialLogin(
    provider: SocialAuthProvider,
    providerAccessToken: string,
  ): Observable<Result<User>> {
    this.isLoadingSubject.next(true);
    return this.userHttpService.socialLogin(provider, providerAccessToken).pipe(
      map((response: SocialAuthResponseDto) => {
        this.localStorageService.setTokenFromLocalStorage(response.token);
        return response.token;
      }),
      switchMap((token: string) => this.getUserApi(token)),
      catchError((err) => {
        var result = new Result<User>();
        var errorBuilder = new ErrorModel.Builder();

        var errorMessage = this.getNonFieldError(err?.error?.non_field_errors);
        if (errorMessage) {
          errorBuilder.unknownErrorKey = errorMessage;
        }

        result.error = errorBuilder.build(err?.status);
        this.onError(result.error);

        return of(result);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // public methods
  login(email: string, password: string): Observable<Result<User>> {
    this.isLoadingSubject.next(true);
    return this.userHttpService.login(email, password).pipe(
      map((tokenDto: TokenDto) => {
        this.localStorageService.setTokenFromLocalStorage(tokenDto.token);
        return tokenDto.token;
      }),
      switchMap((token: string) => this.getUserApi(token)),
      catchError((err) => {
        var result = new Result<User>();
        var errorBuilder = new ErrorModel.Builder();

        const errorMessage = this.getNonFieldError(err?.error?.non_field_errors)
          ?? "ERROR.AUTH_CREDENTIALS";
        errorBuilder.validationErrorKey = errorMessage;
        errorBuilder.unknownErrorKey = errorMessage;
        
        result.error = errorBuilder.build(err.status ?? err.status_code);
        this.onError(result.error);

        return of(result);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  createBooth(token: string, name: string): Observable<Result<boolean>> {
    this.isLoadingSubject.next(true);
    this.getUser();
    var result = new Result<boolean>();

    return this.userHttpService.createBooth(token, name).pipe(
      map((boothDto: BoothDto) => {
        if (boothDto && boothDto.id) {
          result.result = true;
        } else {
          result.result = false;
        }
        return result;
      }),
      catchError((err)=> {
        var result = new Result<boolean>();
        var errorBuilder = new ErrorModel.Builder();

        var errorMessage = this.getNonFieldError(err?.error?.non_field_errors);

        if (errorMessage) {
          errorBuilder.validationErrorKey = errorMessage;
          errorBuilder.unknownErrorKey = errorMessage;
        }
        
        result.error = errorBuilder.build(err.status ?? err.status_code);
        this.onError(result.error);

        return of(result);
      }),
      finalize(() => {this.isLoadingSubject.next(false)})
      )
  }

  getUserApi(token: string): Observable<Result<User>> {
    this.isLoadingSubject.next(true);
    return this.userHttpService.getUser().pipe(
      map((userDto: UserDto) => {
        var result = new Result<User>()

        if (userDto) {
          const user = this.userMapperService.userDtoToUser(userDto, token)
          this.sessionStorageService.setUser(user)
          this.currentUserSubject.next(user);
          result.result = user
          return result;
        } else {
          this.logout();
        }
        // FIXME implement catchError method
        result.error = ErrorModel.UNKNOWN_ERROR;
        return result;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // TODO rename getUser()
  getUserByToken(): Observable<UserType> {
    const token = this.localStorageService.getTokenFromLocalStorage();

    if (!token) {
      return of(undefined);
    }

    
    this.isLoadingSubject.next(true);
    return this.userHttpService.getUser().pipe(
      map((userDto: UserDto) => {
        if (userDto) {
          const user = this.userMapperService.userDtoToUser(userDto, token)
          this.sessionStorageService.setUser(user)
          this.currentUserSubject.next(user);

          return user;
        } else {
          this.logout();
        }

        return undefined;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }



  // need create new user then login
  registration(
    user_name: string,
    user_last_name: string,
    user_email: string,
    user_password: string,
    user_marketing: boolean,
    center_name: string,
    center_web: string,
    center_vat: string,
    center_address_1: string,
    center_address_2: string | null,
    center_postal_code: string,
    center_country: string,
    language: string = 'en'): Observable<Result<Boolean>> {
    this.isLoadingSubject.next(true);
    
    var userDto = new UserDto()
    var centerDto = new CenterDto()

    userDto.name = user_name;
    userDto.last_name = user_last_name;
    userDto.email = user_email;
    userDto.password = user_password;
    userDto.is_marketing_allowed = user_marketing;
    userDto.accepted_terms = true;
    userDto.accepted_privacy_policy = true;
    userDto.language = language.split('-')[0];

    centerDto.name = center_name;
    centerDto.web = center_web.trim();
    centerDto.vat = center_vat;
    centerDto.address_1 = center_address_1;
    if (center_address_2?.trim()) {
      centerDto.address_2 = center_address_2.trim();
    }
    centerDto.postal_code = center_postal_code;
    centerDto.country_iso = center_country;

    userDto.center = centerDto;

    return this.userHttpService.createUser(userDto).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
        var result = new Result<Boolean>();
        result.result = true;

        return result;
      }),
      // TODO call /me switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        var result = new Result<boolean>();
        result.error = this.getRegistrationErrorModel(err);
        this.onError(result.error);

        return of(result);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  private getRegistrationErrorModel(err: any): ErrorModel {
    const hasExistingEmailError = this.containsErrorKey(
      err?.error,
      ['ERROR.AUTH_EMAIL_EXISTS', 'ERROR_AUTH_EMAIL_EXISTS']
    );

    if (hasExistingEmailError) {
      return new ErrorModel(
        'ERROR.AUTH_EMAIL_EXISTS',
        '',
        ErrorModel.UNKNOWN_ERROR.level
      );
    }

    return ErrorModel.UNKNOWN_ERROR;
  }

  private containsErrorKey(value: unknown, keys: string[]): boolean {
    if (typeof value === 'string') {
      return keys.includes(value);
    }
    if (Array.isArray(value)) {
      return value.some(item => this.containsErrorKey(item, keys));
    }
    if (value && typeof value === 'object') {
      return Object.values(value).some(
        item => this.containsErrorKey(item, keys)
      );
    }
    return false;
  }

  forgotPassword(email: string, language: string = 'en'): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.userHttpService
      .forgotPassword(email, language)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  resetPassword(
    newPassword: String,
    uid: String,
    token: String
  ): Observable<Result<boolean>> {
    this.isLoadingSubject.next(true);
    return this.userHttpService
      .resetPassword(
        new ResetPasswordDto(
          uid,
          token,
          newPassword
        )
      ).pipe(
        map(resetPasswordDto => {
          var result = new Result<boolean>();
          if (resetPasswordDto) {
            result.result = true;
          } else {
            result.result = false;
          }
          return result;
        }),
        catchError((err)=> {
          var result = new Result<boolean>();
          result.error = new ErrorModel.Builder().build(err.status);
  
          this.onError(result.error);
          this.isLoadingSubject.next(false);
          
          return of(result);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      )
  }

  verifyEmail(
    uid: String,
    token: String
  ): Observable<Result<boolean>> {
    this.isLoadingSubject.next(true);
    return this.userHttpService
      .verifyEmail(
        new VerifyEmailDto(
          uid,
          token
        )
      ).pipe(
        map(verifyEmailDto => {
          var result = new Result<boolean>();
          if (verifyEmailDto) {
            result.result = true;
          } else {
            result.result = false;
          }
          return result;
        }),
        catchError((err)=> {
          var result = new Result<boolean>();
          result.error = new ErrorModel.Builder().build(err.status);
  
          this.onError(result.error);
          this.isLoadingSubject.next(false);
          
          return of(result);
        }),
        finalize(() => this.isLoadingSubject.next(false))
      )
  }

  getDiscourseConnectRedirect(
    sso: string,
    sig: string
  ): Observable<DiscourseConnectResponse> {
    this.getUser();
    return this.userHttpService.getDiscourseConnectRedirect(
      sso,
      sig
    );
  }

  uploadImage(file: File): Observable<CenterImageUploadResponse> {
    this.isLoadingSubject.next(true);
    this.getUser();
    return this.userHttpService.uploadImage(file).pipe(
      map((response) => {
        if (response?.image) {
          const currentUser = this.getUser();
          const updatedUser = {
            ...currentUser,
            center: {
              ...currentUser.center,
              image: response.image,
            },
          } as User;

          this.sessionStorageService.setUser(updatedUser);
          this.currentUserSubject.next(updatedUser);
        }

        return response;
      }),
      catchError((err) => {
        const error = ErrorModel.fromHttpError(err);
        this.onError(error);
        return throwError(() => error);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }
  

  

  patchUser(center: object): Observable<Result<boolean>> {
    this.getUser();
    return this.userHttpService.patchCenter(center).pipe(
      map((centerDto) => {
        const updatedUser = {
          ...this.currentUserValue,
          center: this.userMapperService.centerDtoToCenter(centerDto),
        } as User;
        this.sessionStorageService.setUser(updatedUser);
        this.currentUserSubject.next(updatedUser);
        const result = new Result<boolean>();
        result.result = true;
        return result;
      }),
      catchError((err) => {
        const result = new Result<boolean>();
        result.error = ErrorModel.fromHttpError(err);
        this.onError(result.error);
        return of(result);
      })
    );
  }


  patchUserProfile(updatedUser: object): Observable<Result<User>> {
    return this.userHttpService.patchUserProfile(updatedUser).pipe(
      map((userDto: UserDto) => {
        const mappedUser = this.userMapperService.userDtoToUser(userDto, this.getUser().token);
        this.currentUserSubject.next(mappedUser); // Actualiza el usuario actual en el Subject
        const result = new Result<User>();
        result.result = mappedUser;
        return result;
      }),
      catchError((err) => {
        const result = new Result<User>();
        result.error = new ErrorModel.Builder().build(err.status);
        this.onError(result.error);
        return of(result);
      })
    );
  }


}
